import React, { useEffect } from 'react';
import { EmergencyMode, ControlMode, Blinker, Gear, DriveMode } from 'core/models/drive';
import { DriveSwitch as DriveSwitchView, DriveSwitchProps as ViewProps } from 'components/views/organisms/DriveSwitch';
import { useVehicleControllerStatus, useVehicleController } from 'services/useVehicleControllerStatus';
import { useVehicleStatus } from 'services/useVehicleStatus';
import { Vehicle } from 'core/models/vehicle';
import { Project } from 'core/models/project';
import { VehicleController } from 'core/repositories/VehicleController';
import { useScopeCheck } from 'services/useScope';

function useCancelRemoteModeByManualOverride(controller: VehicleController | null, currentFrontendMode: ControlMode, currentRemoteMode: DriveMode) {
    useEffect(() => {
        if (!controller) return;

        if (currentFrontendMode == ControlMode.Remote && currentRemoteMode == DriveMode.Manual) {
            controller.pushEvent({ eventType: 'controlMode', controlMode: ControlMode.Auto });
        }
    }, [controller, currentRemoteMode, currentFrontendMode]);
}

export type DriveSwitchProps = {
    className?: string;
    vehicleId: Vehicle.Id;
    projectId: Project.Id;
}

export const DriveSwitch = (props: DriveSwitchProps) => {
    const { vehicleId, projectId } = props;

    const controllerStatus = useVehicleControllerStatus();
    const controller = useVehicleController();
    const vehicleStatus = useVehicleStatus(vehicleId, projectId);
    const { mode: driveMode = DriveMode.Auto, emergency = false } = vehicleStatus;

    const isControlAllowed = useScopeCheck('sendVehicleCommandScope') ?? false;

    useCancelRemoteModeByManualOverride(controller, controllerStatus.controlMode, driveMode);

    const _props: ViewProps = {
        emergencyMode: controllerStatus.emergencyMode,
        controlMode: controllerStatus.controlMode,
        blinker: controllerStatus.blinker,
        gear: controllerStatus.gear,
        onEmergencyModeToggle: () => {
            const emergency = controllerStatus.emergencyMode == EmergencyMode.Normal ? EmergencyMode.Emergency : EmergencyMode.Normal;
            controller?.pushEvent({ eventType: 'emergency', emergency });
        },
        onControlModeToggle: () => {
            const controlMode = controllerStatus.controlMode == ControlMode.Remote ? ControlMode.Auto : ControlMode.Remote;
            controller?.pushEvent({ eventType: 'controlMode', controlMode });
        },
        onBlinkerChange: (blinker: Blinker | null) => {
            controller?.pushEvent({ eventType: 'blinker', blinker });
        },
        onSelectGear: (gear: Gear) => {
            controller?.pushEvent({ eventType: 'gear', gear });
        },
        actual: {
            driveMode,
            emergencyMode: emergency ? EmergencyMode.Emergency : EmergencyMode.Normal,
        },
        isControlAllowed,
    };

    return (
        <DriveSwitchView className={props.className} {..._props} />
    );
};
